import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    FolderOpen, Search, ChevronLeft, User, AlertTriangle, 
    Compass, Sparkles, CheckCircle, AlertCircle, Loader2, 
    Printer, Activity, ChevronRight, MessageSquare, Save, Trash2
} from 'lucide-react';

import { db } from '../firebase'; 
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import EditableAIBox from './EditableAIBox';

const AI_API_URL = "https://generateanalysis-yyf3isw5zq-uc.a.run.app";
const ARCHIVE_CLASS_FOLDER =
    'Arşiv / Güncel Listede Olmayanlar';


// 🚀 1. VERİ STANDARTLAŞTIRICI (Zarf Okuyucu ve Ön Tarayıcı)
const normalizeTestData = (sub) => {
    const rep = sub.reportData || {};
    
    // 🚨 CAN SİMİDİ (PRE-CHECK): Eğer test henüz uzman tarafından incelenmemişse (pending)
    // Yaşam Pencerem'in 18. sorusuna doğrudan bak! Eğer işaretlenmişse sistemi anında kırmızıya çek.
    const isYasam = sub.testId === 'yasam_pencerem' || sub.testId?.includes('yasam');
    const manualCriticalCheck = isYasam && (sub.answers?.['18'] === true || sub.answers?.['18'] === "true" || sub.answers?.['18'] === 1 || sub.answers?.['18'] === "1");

    return {
        ...sub,
        normalizedScore: Number(rep.riskScore) || (manualCriticalCheck ? 100 : 0),
        masterRisk: rep.masterRisk || (manualCriticalCheck ? 'high' : 'unknown'),
        isCritical: !!(rep.isCritical || manualCriticalCheck), // Test okunmasa bile 18. madde kırmızıyı yakar!
        domain: rep.domain || 'psychological'
    };
};

// 🚀 2. PDR SIRALAMA SEKRETERİ (Agresif Risk Tarayıcı)
// Öğrencileri ortalamaya göre değil, "En Yüksek Risklerine" göre sıralar.
const aggregateStudentProfile = (tests) => {
    let isCritical = false;
    let highestRiskScore = 0;
    let validTestCount = 0;
    const domains = { psychological: 0, academic: 0, behavioral: 0, social: 0, demographic: 0 };

    tests.forEach(t => {
        if (t.domain === 'profile') return; // Yetenek/İlgi testleri kriz radarına girmez
        validTestCount++;
        
        if (t.isCritical) isCritical = true; // 18. Madde vb. varsa sistemi anında kilitler
        if (t.normalizedScore > highestRiskScore) highestRiskScore = t.normalizedScore;
        
        // Ortalama tuzağına düşmemek için her alanın sadece EN YÜKSEK riskini tutuyoruz
        if (t.normalizedScore > (domains[t.domain] || 0)) domains[t.domain] = t.normalizedScore;
    });

    // UI'ın (Kutu ve Grafikler) bozulmaması için eski formatta çıkarıyoruz
    const domainNames = { psychological: 'Duygusal', academic: 'Akademik', behavioral: 'Davranışsal', social: 'Sosyal', demographic: 'Demografik' };
    const domainBreakdown = Object.entries(domains)
        .filter(([_, score]) => score > 0)
        .map(([key, score]) => ({ name: domainNames[key] || key, score }));

    const patterns = [];
    if (domains.psychological >= 70 && domains.academic >= 70) patterns.push("Duygusal ve Akademik Düşüş Çakışması");
    if (domains.behavioral >= 70 && domains.academic < 50) patterns.push("Akademik Odaktan Bağımsız Davranışsal Risk");
    if (domains.psychological >= 70 && domains.behavioral >= 70) patterns.push("Duygusal ve Davranışsal Risk Ortaklığı");

    return {
        domainScores: domains,
        highestRiskScore,
        masterRisk: isCritical ? 'critical' : (highestRiskScore >= 70 ? 'high' : (highestRiskScore >= 40 ? 'medium' : (highestRiskScore > 0 ? 'low' : 'unknown'))),
        isCritical,
        confidenceScore: Math.min(100, validTestCount * 33),
        patterns,
        domainBreakdown
    };
};



const StudentFolders = ({
  submissions,
  onAnalyze,
  user,
  academicYear = '2026-2027'
}) => {

    const [selectedClassFolder, setSelectedClassFolder] = useState(null); 
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [compassLoading, setCompassLoading] = useState(false);
    const [compassResult, setCompassResult] = useState(null);
    const [copilotLoading, setCopilotLoading] = useState(false);
    const [copilotResult, setCopilotResult] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [allClasses, setAllClasses] = useState([]);
    
   // 🔥 PDR DANIŞMAN KAYIT DEFTERİ SİSTEMİ
    const [counselorNote, setCounselorNote] = useState("");
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [studentNotes, setStudentNotes] = useState([]); // Eski görüşmeleri tutacağımız dizi

    // Öğrenci seçildiğinde eski notlarını Firebase'den çek
    // Öğrenci seçildiğinde eski notlarını Firebase'den çek
  useEffect(() => {

    if (
        !selectedStudent ||
        !user?.uid
    ) {
        setStudentNotes([]);
        setCounselorNote("");

        return;
    }


    const normalizedStudentNo =
        String(
            selectedStudent.no ?? ''
        ).trim();


    // ============================================================
    // PDR GÖRÜŞME KAYITLARI — SINIFTAN BAĞIMSIZ OKUMA
    // ============================================================
    //
    // Yeni kayıtlar:
    //   studentNo alanı üzerinden eşleşir.
    //
    // Eski kayıtlar:
    //   B1_125, A1_125, D_125 gibi eski studentKey
    //   değerlerinin sonundaki öğrenci numarasından bulunur.
    //
    // Böylece eski veriyi migrate etmeden geriye uyumluluk korunur.
    // ============================================================

    const q =
        query(
            collection(
                db,
                "counselorNotes"
            ),

            where(
                "clinicianUid",
                "==",
                user.uid
            )
        );


    const unsubscribe =
        onSnapshot(
            q,

            snapshot => {

                const notes =
                    snapshot.docs
                        .map(
                            noteDoc => ({
                                id:
                                    noteDoc.id,

                                ...noteDoc.data()
                            })
                        )
                        .filter(
                            note => {

                                const noteYear =
                                    note.academicYear ||
                                    '2025-2026';


                                if (
                                    noteYear !==
                                    academicYear
                                ) {
                                    return false;
                                }


                                const noteStudentNo =
                                    String(
                                        note.studentNo ??
                                        ''
                                    ).trim();


                                // Yeni kayıt formatı
                                if (
                                    noteStudentNo &&
                                    noteStudentNo ===
                                    normalizedStudentNo
                                ) {
                                    return true;
                                }


                                // Eski kayıt formatı:
                                // B1_125, A1_125, D_125...
                                const legacyStudentKey =
                                    String(
                                        note.studentKey ??
                                        ''
                                    ).trim();


                                return (
                                    legacyStudentKey
                                        .endsWith(
                                            `_${normalizedStudentNo}`
                                        )
                                );
                            }
                        );


                notes.sort(
                    (a, b) =>
                        (
                            b.timestamp
                                ?.seconds ||
                            0
                        ) -
                        (
                            a.timestamp
                                ?.seconds ||
                            0
                        )
                );


                setStudentNotes(
                    notes
                );
            },

            error => {

                console.error(
                    "Notlar çekilirken hata oluştu:",
                    error
                );
            }
        );


    return () =>
        unsubscribe();

}, [
    selectedStudent,
    user,
    academicYear
]);

    // Yeni notu Firebase'e kalıcı olarak kaydet
const handleSaveNote =
    async () => {

        if (
            !counselorNote.trim() ||
            !selectedStudent ||
            !user?.uid
        ) {
            return;
        }


        setIsSavingNote(true);


        try {

            const normalizedStudentNo =
                String(
                    selectedStudent.no ?? ''
                ).trim();


            // =====================================================
            // YENİ ÖĞRENCİ KİMLİĞİ
            // =====================================================
            //
            // Kimlik artık sınıfa bağlı değildir.
            //
            // clinicianUid
            // + academicYear
            // + studentNo
            //
            // öğrencinin kalıcı kimliğini oluşturur.
            //
            // studentClassAtTime yalnız tarihsel bilgi olarak tutulur.
            // =====================================================

            await addDoc(
                collection(
                    db,
                    "counselorNotes"
                ),

                {
                    studentKey:
                        `${academicYear}_${normalizedStudentNo}`,

                    studentNo:
                        normalizedStudentNo,

                    studentName:
                        selectedStudent.name,

                    studentClassAtTime:
                        selectedStudent.class,

                    clinicianUid:
                        user.uid,

                    academicYear:
                        academicYear,

                    note:
                        counselorNote.trim(),

                    timestamp:
                        serverTimestamp()
                }
            );


            setCounselorNote("");

        } catch (error) {

            console.error(
                "Not kaydedilemedi:",
                error
            );

            alert(
                "Not kaydedilirken bir hata oluştu!"
            );

        } finally {

            setIsSavingNote(false);
        }
    };


// 🔥 PDR DANIŞMAN NOTU SİLME SİSTEMİ
    const handleDeleteNote = async (noteId) => {
        // Yanlışlıkla silinmesini engellemek için onay kutusu
        const isConfirmed = window.confirm("Bu görüşme kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.");
        if (!isConfirmed) return;

        try {
            await deleteDoc(doc(db, "counselorNotes", noteId));
            // Not: Başka bir şey yapmana gerek yok. onSnapshot (useEffect) sildiğini görüp ekranı anında güncelleyecek!
        } catch (error) {
            console.error("Not silinirken hata oluştu:", error);
            alert("Kayıt silinemedi. Lütfen yetkiniz olduğundan emin olun.");
        }
    };
    
    const abortControllerRef = useRef(null);

    const handlePrint = (elementId, title) => {
        setDownloadingPdf(true);
        const content = document.getElementById(elementId).innerHTML;
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.bottom = '0';
        iframe.style.right = '0';
        iframe.style.width = '1024px';
        iframe.style.height = '100vh';
        iframe.style.opacity = '0.01';
        iframe.style.pointerEvents = 'none';
        iframe.style.zIndex = '-1';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        let stylesHTML = '';
        document.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => {
            stylesHTML += el.outerHTML;
        });

        doc.open();
        doc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${selectedStudent?.name} - ${title}</title>
              ${stylesHTML}
              <style>
                @media print {
                  @page { size: A4 portrait; margin: 15mm; }
                  body { font-family: 'Segoe UI', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
                  .no-print { display: none !important; }
                }
              </style>
            </head>
            <body class="bg-white text-black p-4">
              ${content}
            </body>
          </html>
        `);
        doc.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        setDownloadingPdf(false);
        setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 1500); 
    };

    useEffect(() => {
        if (user && user.uid) {
            const q = query(collection(db, "classes"), where("clinicianUid", "==", user.uid));
            const unsubscribe = onSnapshot(q, (snapshot) => {

    const myClasses = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(cls => {
            const classYear = cls.academicYear || '2025-2026';
            return classYear === academicYear;
        });

    setAllClasses(myClasses);
});
            return () => unsubscribe();
        }
    }, [user, academicYear]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (selectedStudent) {
            setCounselorNote(""); 
            setCopilotResult(null); // Yeni öğrenciye geçince eski AI Psikososyal Raporunu sil
            setCompassResult(null); // Eski Kariyer Pusulasını sil
        }
    }, [selectedStudent]);

   

   const getCurrentStudentLocation = (no) => {

    if (
        !allClasses ||
        allClasses.length === 0
    ) {
        return null;
    }


    const normalizedNo =
        String(no ?? '').trim();


    for (const classItem of allClasses) {

        const students =
            Array.isArray(classItem?.students)
                ? classItem.students
                : [];


        const foundStudent =
            students.find(
                student =>
                    String(
                        student?.no ?? ''
                    ).trim() ===
                    normalizedNo
            );


        if (foundStudent) {

            return {
                className:
                    String(
                        classItem?.name ?? ''
                    ).trim(),

                student:
                    foundStudent
            };
        }
    }


    return null;
};


const getStudentName = (no, className) => {

    const currentLocation =
        getCurrentStudentLocation(no);


    if (currentLocation?.student?.name) {
        return currentLocation.student.name;
    }


    if (
        !allClasses ||
        allClasses.length === 0
    ) {
        return "";
    }


    const foundClass =
        allClasses.find(
            c =>
                String(c.name)
                    .trim()
                    .toUpperCase() ===
                String(className)
                    .trim()
                    .toUpperCase()
        );


    if (!foundClass) {
        return "";
    }


    const foundStudent =
        foundClass.students.find(
            s =>
                String(s.no)
                    .trim() ===
                String(no)
                    .trim()
        );


    return foundStudent
        ? foundStudent.name
        : "";
};

    const { studentsArray, uniqueClasses } = useMemo(() => {
        const studentsMap = {};
        
      submissions.forEach(sub => {

    if (sub.status !== 'reported') {
        return;
    }


    if (
        sub.studentClass === 'Öğretmen' ||
        sub.studentClass === 'Veli' ||
        String(sub.studentNo).includes('Veli')
    ) {
        return;
    }


    // ============================================================
    // GÜNCEL SINIF ÇÖZÜMLEME
    // ============================================================
    //
    // Test geçmişindeki studentClass korunur.
    // Ancak Öğrenci Dosyaları ekranındaki klasör,
    // güncel okul listesindeki sınıfa göre belirlenir.
    //
    // Öğrenci güncel listede bulunamazsa şimdilik
    // eski sınıf bilgisi korunur.
    // Bir sonraki adımda bunları Arşiv klasörüne taşıyacağız.
    // ============================================================

    const currentLocation =
        getCurrentStudentLocation(
            sub.studentNo
        );


    const hasCurrentClassList =
    Array.isArray(allClasses) &&
    allClasses.length > 0;


const resolvedClass =
    currentLocation?.className ||
    (
        hasCurrentClassList
            ? ARCHIVE_CLASS_FOLDER
            : sub.studentClass
    );


    const key =
        `${resolvedClass}_${sub.studentNo}`;


    if (!studentsMap[key]) {

        const realName =
            currentLocation
                ?.student
                ?.name ||
            sub.studentName ||
            getStudentName(
                sub.studentNo,
                resolvedClass
            ) ||
            "İsimsiz Öğrenci";


        studentsMap[key] = {

            id:
                key,

            class:
                resolvedClass,

            no:
                sub.studentNo,

            name:
                realName,

            tests:
                [],

            holland:
                null,

            coklu_zeka:
                null,

            mesleki_degerler:
                null,

            riskBadges:
                []
        };
    }


    // Geçmiş test kaydı kendi orijinal studentClass
    // bilgisini korur; yalnız klasör güncel sınıfa taşınır.
    const normalizedTest =
        normalizeTestData(sub);


    studentsMap[key]
        .tests
        .push(
            normalizedTest
        );


    if (
        normalizedTest
            .testId
            ?.includes('holland')
    ) {
        studentsMap[key].holland =
            normalizedTest;
    }


    if (
        normalizedTest
            .testId
            ?.includes('coklu_zeka')
    ) {
        studentsMap[key].coklu_zeka =
            normalizedTest;
    }


    if (
        normalizedTest
            .testId
            ?.includes('degerler')
    ) {
        studentsMap[key].mesleki_degerler =
            normalizedTest;
    }
});

        Object.values(studentsMap).forEach(student => {
            student.masterProfile = aggregateStudentProfile(student.tests);
        });

        const arr = Object.values(studentsMap).sort((a, b) => {
            if (a.masterProfile.isCritical && !b.masterProfile.isCritical) return -1;
            if (!a.masterProfile.isCritical && b.masterProfile.isCritical) return 1;
            
            if (b.masterProfile.highestRiskScore !== a.masterProfile.highestRiskScore) {
                return b.masterProfile.highestRiskScore - a.masterProfile.highestRiskScore;
            }
            return a.class.localeCompare(b.class) || String(a.no).localeCompare(String(b.no), undefined, { numeric: true });
        });

        const classes =
    [...new Set(arr.map(s => s.class))]
        .sort(
            (a, b) => {

                if (
                    a === ARCHIVE_CLASS_FOLDER
                ) {
                    return 1;
                }

                if (
                    b === ARCHIVE_CLASS_FOLDER
                ) {
                    return -1;
                }

                return String(a)
                    .localeCompare(
                        String(b),
                        'tr',
                        {
                            numeric: true
                        }
                    );
            }
        );


return {
    studentsArray: arr,
    uniqueClasses: classes
};
    }, [submissions, allClasses]);

    const handleGenerateCompass = async (profile) => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setCompassLoading(true);
        setCompassResult(null);

        try {
            const hTop1 = profile.holland?.reportData?.chartData?.sort((a,b)=>b.A - a.A)[0]?.subject || "Belirsiz";
            const zTop1 = profile.coklu_zeka?.reportData?.topIntelligences?.[0]?.subject || "Belirsiz";
            const dTop1 = profile.mesleki_degerler?.reportData?.topValues?.[0]?.subject || "Belirsiz";
            const dTop2 = profile.mesleki_degerler?.reportData?.topValues?.[1]?.subject || "Belirsiz";

            const prompt = `SEN BİR KARİYER VE GELECEK TASARIM UZMANISIN. Karşındaki öğrenci Kariyer Yöneliminin 'Altın Üçgen' testlerini tamamladı.
            ÖĞRENCİNİN PROFİLİ (ALTIN ÜÇGEN):
            1. İLGİ ALANI (Holland): ${hTop1}
            2. YETENEK / ZEKASI (Çoklu Zeka): ${zTop1}
            3. MESLEKİ DEĞERLERİ / MOTİVASYONU: ${dTop1} ve ${dTop2}
            
            GÖREV: Bu 3 veriyi TEK BİR POTADA eriterek, öğrenci ve ailesi için şık bir "Kariyer Pusulası Dergisi" makalesi yaz.
            KESİN KURALLAR: Sadece şu 4 alt başlığı kullan:
            **1. ALTIN ÜÇGEN ANALİZİ:** (İlgisi, zekası ve değerlerinin nasıl bir insan profili oluşturduğunu anlat.)
            **2. İDEAL ÇALIŞMA EKOSİSTEMİ:** (Fiziki ve psikolojik çalışma ortamı nasıl olmalı?)
            **3. NOKTA ATIŞI KARİYER ROTALARI:** (Profile %100 uyan 4-5 modern meslek.)
            **4. LİSE VE ÜNİVERSİTE İÇİN GELİŞİM ROTASI:** (Hobiler ve eğitim tavsiyeleri.)`;

            const res = await fetch(AI_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                signal: signal,
                body: JSON.stringify({
                    studentName: `${profile.class} - ${profile.no}`,
                    testName: "ALTIN ÜÇGEN KARİYER PUSULASI",
                    riskLevel: "ÖZEL DERGİ",
                    markedItems: [prompt]
                })
            });

            if (!res.ok) throw new Error("Sunucu hatası");
            const data = await res.json();
            setCompassResult(data.result);

        } catch (error) {
            if (error.name === 'AbortError') return;
            alert("Pusula üretilirken hata oluştu: " + error.message);
        } finally {
            setCompassLoading(false);
        }
    };

    const handleGenerateCopilot = async (profile) => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setCopilotLoading(true);
        setCopilotResult(null);

        try {
            // 🔥 DÜZELTME 1: Tüm geçmiş danışman notlarını tek bir metinde birleştiriyoruz!
            const allNotesText = studentNotes.map((sn, idx) => 
                `${studentNotes.length - idx}. Görüşme (${new Date(sn.timestamp?.seconds * 1000).toLocaleDateString('tr-TR')}): ${sn.note}`
            ).join('\n');
            
            // Eğer kutuda henüz kaydedilmemiş bir yazı varsa onu da hesaba katalım
            const activeNote = counselorNote.trim() ? `\nYeni/Kaydedilmemiş Gözlem: ${counselorNote.trim()}` : "";
            const combinedNotes = (allNotesText + activeNote).trim() || "Danışman notu girilmemiştir.";

            // Uzman notlarındaki gizli riskleri tarayan sistem
            const noteFlags = [];
            const noteLower = combinedNotes.toLowerCase();
            if (/öfke|şiddet|kavga|saldırgan|sinir/i.test(noteLower)) noteFlags.push("Öfke Eğilimi (Uzman Gözlemi)");
            if (/yalnız|içine kapanık|arkadaşı yok|sessiz/i.test(noteLower)) noteFlags.push("Sosyal İzolasyon (Uzman Gözlemi)");
            if (/aile|anne|baba|ev|boşanma/i.test(noteLower)) noteFlags.push("Ailevi Dinamikler (Uzman Gözlemi)");
            if (/zarar|ölüm|yaşamak|intihar/i.test(noteLower)) noteFlags.push("HAYATİ RİSK ŞÜPHESİ (Uzman Gözlemi)");

            const processedTests = [...profile.tests].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            let matematikselTrend = "Yeterli veri yok";
            if (processedTests.length > 1) { 
                const firstScore = processedTests[0].normalizedScore || 0;
                const lastScore = processedTests[processedTests.length - 1].normalizedScore || 0;
                const diff = lastScore - firstScore;

                if (diff >= 15) matematikselTrend = "CİDDİ ARTIŞ 📈 (Risk Büyüyor)";
                else if (diff > 0) matematikselTrend = "HAFİF ARTIŞ ↗️";
                else if (diff <= -15) matematikselTrend = "BELİRGİN DÜŞÜŞ 📉 (İyileşme)";
                else if (diff < 0) matematikselTrend = "HAFİF DÜŞÜŞ ↘️";
                else {
                    if (lastScore >= 70) matematikselTrend = processedTests.length >= 3 ? "KRONİK YÜKSEK RİSK ⚠️ (Süreklilik var)" : "YÜKSEK RİSK ⚠️";
                    else matematikselTrend = "STABİL 🟡";
                }
            }

            // 🔥 DÜZELTME 2: 18. Madde ve diğer kritik maddeleri "Zaman Kapsülüne" ekliyoruz
            const timeCapsule = processedTests.map(t => {
                const rep = t.reportData || {};
                const skor = t.normalizedScore !== undefined ? `${t.normalizedScore}/100` : "Belirlenemedi";
                const durum = t.riskLevel === 'high' ? 'YÜKSEK RİSK' : (t.riskLevel === 'medium' ? 'ORTA RİSK' : (t.riskLevel === 'low' ? 'DÜŞÜK RİSK' : 'PROFİL TESTİ'));
                const baskinAlan = rep.topStyle || (rep.chartData && rep.chartData.length > 0 ? rep.chartData[0].subject : "Belirsiz");

                // Kritik madde çıkarıcı (risks dizisi varsa oku)
                let kritikMaddeler = "";
                if (rep.risks && Array.isArray(rep.risks) && rep.risks.length > 0) {
                    const kritikler = rep.risks.filter(r => r.severity === 'critical' || r.isCritical || String(r.id) === "18" || String(r.text).includes("KRİTİK"));
                    if (kritikler.length > 0) {
                        kritikMaddeler = `\n  ÖNEMLİ MADDELER: ` + kritikler.map(k => {
                            const rawText = k.text || String(k);
                            const cleanText = rawText.replace(/[\[\(]?KRİTİK[\]\)]?/gi, "").replace(/^[^a-zA-ZçÇğĞıİöÖşŞüÜ0-9]+/, "").trim();
                            return `[Madde ${k.id || '?'}: ${cleanText}]`;
                        }).join(' | ');
                    }
                }

                return `- TARIH: ${new Date(t.timestamp).toLocaleDateString('tr-TR')} | TEST: ${t.testName} | DURUM: ${durum} | BASKIN ALAN: ${baskinAlan} | SKOR: ${skor}${kritikMaddeler}`;
            }).join('\n\n');

            const prompt = `SEN UZMAN BİR OKUL PSİKOLOJİK DANIŞMANISIN.
Karşındaki öğrenci "${profile.class} - ${profile.name}". 

🚨 REHBER360 ÇOK BOYUTLU PDR ANALİZİ:
- En Yüksek Risk Skoru: ${profile.masterProfile.highestRiskScore}/100 (${profile.masterProfile.masterRisk.toUpperCase()})
- Acil Destek İhtiyacı: ${profile.masterProfile.isCritical ? 'VAR (Öncelikli Vaka)' : 'YOK'}
- Veri Güvenilirliği: %${profile.masterProfile.confidenceScore}
- Matematiksel Trend: ${matematikselTrend}
- Alan Bazlı Skorlar: Duygusal: ${profile.masterProfile.domainScores.psychological}, Davranışsal: ${profile.masterProfile.domainScores.behavioral}, Akademik: ${profile.masterProfile.domainScores.academic}
- Tespit Edilen Desenler: ${[...profile.masterProfile.patterns, ...noteFlags].join(' | ') || 'Belirgin bir desen yok'}

✍️ PSİKOLOJİK DANIŞMANIN GÖZLEM VE NOTLARI (BUNU MERKEZE AL):
${combinedNotes}

ÖĞRENCİ TEST GEÇMİŞİ VE İŞARETLEDİĞİ ÖNEMLİ MADDELER:
${timeCapsule}

GÖREVİN: Danışmanın geçmişteki tüm görüşme notlarını ve sistemdeki test verilerini sentezleyerek uygulanabilir bir Psikososyal Destek Planı oluştur. Kesin tanılardan teşhis koymaktan kaçın, PDR jargonunu kullan. Veriler arasında "18. Madde" veya "Hayati Risk" varsa planı acil kriz müdahalesi şeklinde tasarla. Uzman danışman notları var ise analizini mutlaka o notlardaki gerçekçi gözlemlere dayandır.

⚠️ KESİN KURAL: Raporda maddeleri listelerken asla yanlarına "⚠️ [KRİTİK]" veya "(KRİTİK)" gibi etiketler, emojiler veya uyarı işaretleri koyma. Sadece madde numarasını ve ifadenin kendisini resmi bir dille yaz.

LÜTFEN ÇIKTIYI SADECE ŞU BAŞLIKLARLA FORMATLA:
**1. RİSK ÖZETİ VE PSİKOSOSYAL TABLO**
**2. ÖN PLANA ÇIKAN TEMEL BULGULAR VE TEMATİK ANALİZ** (Dikkat: Eğer öğrenci çok fazla madde işaretlediyse hepsini tek tek yazıp raporu uzatma. Benzer maddeleri 'Aile Dinamikleri', 'Duygusal Zorluklar' gibi temalar altında grupla. Sadece en acil ve kritik olan 3-4 maddeyi 'Bulgu ve PDR Açıklaması' formatında detaylandır, gerisini özetle.)
**3. DANIŞMAN NOTU İLE ÇELİŞEN VEYA ÖRTÜŞEN NOKTALAR**
**4. ÖNCELİKLİ MÜDAHALE ALANLARI**
**5. 4 HAFTALIK KISA VE NET EYLEM PLANI**`;

            const res = await fetch(AI_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                signal: signal,
                body: JSON.stringify({
                    studentName: `${profile.class} - ${profile.no}`,
                    testName: "PDR UZMAN ANALİZİ",
                    riskLevel: "PSİKOSOSYAL DESTEK",
                    markedItems: [prompt]
                })
            });

            if (!res.ok) throw new Error("Sunucu hatası");
            const data = await res.json();
            setCopilotResult(data.result);

        } catch (error) {
            if (error.name === 'AbortError') return;
            alert("Analiz hatası: " + error.message);
        } finally {
            setCopilotLoading(false);
        }
    };

    // --- EKRAN 3: ÖĞRENCİ DOSYASI (İÇİ) ---
    if (selectedStudent) {
        const isReady = selectedStudent.holland && selectedStudent.coklu_zeka && selectedStudent.mesleki_degerler;
        return (
            <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                   <button onClick={() => { setSelectedStudent(null); setCompassResult(null); setCopilotResult(null); }} className="flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 transition">
                        <ChevronLeft className="h-4 w-4 mr-1"/> {selectedClassFolder} Sınıfına Dön
                    </button>
                    <div className="text-right">
                        <h2 className="text-2xl font-black text-slate-800 flex items-center justify-end"><User className="h-6 w-6 mr-2 text-slate-400"/> {selectedStudent.name}</h2>
                        <p className="text-sm font-bold text-slate-500">{selectedStudent.class} - No: {selectedStudent.no}</p>
                    </div>
                </div>

                {selectedStudent.masterProfile?.isCritical ? (
                    <div className="p-5 bg-red-600 rounded-2xl shadow-lg border border-red-700 flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4">
                        <div className="flex items-center text-white">
                            <AlertTriangle className="h-10 w-10 mr-4 animate-pulse" />
                            <div>
                                <h3 className="font-black text-lg uppercase tracking-wider">Acil Müdahale (Öncelikli Risk)</h3>
                                <p className="text-red-100 text-xs font-medium mt-1">Hayati risk şüphesi veya öncelikli madde (18. Madde vb.) tespit edildi.</p>
                            </div>
                        </div>
                    </div>
                ) : selectedStudent.masterProfile?.masterRisk === 'high' ? (
                    <div className="p-5 bg-rose-50 rounded-2xl shadow-sm border border-rose-200 flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4">
                        <div className="flex items-center text-rose-700">
                            <AlertTriangle className="h-8 w-8 mr-4" />
                            <div>
                                <h3 className="font-black text-lg uppercase tracking-wider">Yüksek Risk Uyarısı</h3>
                                <p className="text-rose-600 text-xs font-medium mt-1">Öğrencinin envanter sonuçlarında yüksek risk tespit edilmiştir. Gözlem gerektirir.</p>
                            </div>
                        </div>
                    </div>
                ) : selectedStudent.masterProfile?.masterRisk === 'medium' ? (
                    <div className="p-5 bg-amber-50 rounded-2xl shadow-sm border border-amber-200 flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4">
                        <div className="flex items-center text-amber-700">
                            <AlertCircle className="h-8 w-8 mr-4" />
                            <div>
                                <h3 className="font-black text-lg uppercase tracking-wider">Orta Seviye Risk (İzleme)</h3>
                                <p className="text-amber-600 text-xs font-medium mt-1">Öğrencinin sonuçlarında bazı alanlarda orta düzeyde dalgalanmalar var.</p>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* 🔥 PSİKOLOJİK DANIŞMAN KAYIT DEFTERİ (LOGBOOK) */}
                <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
                    <h3 className="text-lg font-black text-slate-800 mb-4 flex items-center justify-between">
                        <div className="flex items-center"><MessageSquare className="h-5 w-5 mr-2 text-blue-500"/> PDR Görüşme Kayıtları</div>
                        <span className="text-xs font-bold bg-blue-50 text-blue-600 px-3 py-1 rounded-full">{studentNotes.length} Kayıt</span>
                    </h3>
                    
                    {/* Geçmiş Görüşmeler Listesi */}
                    {studentNotes.length > 0 ? (
                        <div className="mb-6 space-y-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                            {studentNotes.map((sn, index) => (
                                <div key={sn.id} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl relative group">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{studentNotes.length - index}. Görüşme</span>
                                        <div className="flex items-center gap-2">
    <span className="text-[10px] font-bold text-slate-400 bg-white px-2 py-0.5 rounded shadow-sm">
        {sn.timestamp ? new Date(sn.timestamp.seconds * 1000).toLocaleDateString('tr-TR') : 'Şimdi eklendi'}
    </span>
    <button 
        onClick={() => handleDeleteNote(sn.id)}
        className="text-slate-300 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50"
        title="Bu Kaydı Sil"
    >
        <Trash2 className="h-4 w-4" />
    </button>
</div>
                                    </div>
                                    <p className="text-sm font-medium text-slate-700 whitespace-pre-wrap">{sn.note}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mb-6 p-6 text-center border border-dashed border-slate-200 rounded-2xl bg-slate-50">
                            <p className="text-sm font-bold text-slate-400">Bu öğrenci için henüz bir görüşme notu girilmemiş.</p>
                        </div>
                    )}

                    {/* Yeni Not Ekleme Alanı */}
                    <div className="relative">
                        <textarea 
                            className="w-full h-24 p-4 bg-white border-2 border-slate-100 focus:border-blue-500 rounded-2xl text-sm outline-none transition-all placeholder:text-slate-400 font-medium resize-none shadow-inner"
                            placeholder="Yeni görüşme notunu veya gözleminizi buraya ekleyin..."
                            value={counselorNote}
                            onChange={(e) => setCounselorNote(e.target.value)}
                        />
                        <div className="flex justify-end mt-3">
                            <button 
                                onClick={handleSaveNote} 
                                disabled={isSavingNote || !counselorNote.trim()}
                                className="flex items-center px-5 py-2.5 bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl text-xs font-bold hover:bg-slate-900 transition shadow-sm"
                            >
                                {isSavingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
                                {isSavingNote ? 'Kaydediliyor...' : 'Görüşmeyi Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* PDR UZMAN ANALİZİ (CO-PILOT) */}
                <div className="bg-rose-900 text-white rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden mt-6">
                    <div className="absolute top-0 right-0 -mt-8 -mr-8 text-white/5 pointer-events-none">
                        <Activity className="h-48 w-48"/>
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-xl font-black mb-1 flex items-center"><Sparkles className="h-5 w-5 mr-2 text-rose-300"/> PDR Karar Destek Asistanı</h3>
                        <p className="text-rose-200 text-sm font-medium mb-6">Öğrencinin test geçmişini ve danışman notlarını sentezleyerek plan oluşturur.</p>
                        
                        <button onClick={() => handleGenerateCopilot(selectedStudent)} disabled={copilotLoading || selectedStudent.tests.length === 0} className="w-full py-4 bg-white hover:bg-rose-50 text-rose-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-black transition shadow-lg flex items-center justify-center">
                            {copilotLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin"/> : <Activity className="h-5 w-5 mr-2"/>}
                            {copilotLoading ? 'Plan Sentezleniyor...' : 'Psikososyal Destek Planı Üret'}
                        </button>
                    </div>
                </div>

                {copilotResult && (
                    <div id="copilot-print-area" className="bg-white p-8 rounded-3xl border border-rose-100 shadow-sm mt-6 print:border-none print:shadow-none print:p-0">
                        <div className="border-b border-slate-100 pb-4 mb-6 flex justify-between items-center print:border-b-2 print:border-rose-900">
                            <div>
                                <h3 className="text-2xl font-black text-rose-900 uppercase">Psikososyal Destek Planı</h3>
                                <p className="text-slate-500 text-sm mt-1 font-bold print:text-rose-700">{selectedStudent.class} - {selectedStudent.name}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handlePrint('copilot-print-area', 'Destek Planı')} disabled={downloadingPdf} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs flex items-center transition shadow-md no-print">
                                    {downloadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Printer className="h-4 w-4 mr-2"/>}
                                    PDF / Yazdır
                                </button>
                            </div>
                        </div>
                        <div className="prose prose-rose max-w-none prose-h1:text-rose-900 prose-h2:text-rose-800 prose-p:text-slate-700 mb-8">
                            <EditableAIBox text={copilotResult} />
                        </div>
                    </div>
                )}

                {/* ALTIN ÜÇGEN KARİYER PUSULASI */}
                <div className="bg-indigo-900 text-white rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden mt-6">
                    <div className="absolute top-0 right-0 -mt-8 -mr-8 text-white/5 pointer-events-none"><Compass className="h-48 w-48"/></div>
                    <div className="relative z-10">
                        <h3 className="text-xl font-black mb-1 flex items-center"><Sparkles className="h-5 w-5 mr-2 text-indigo-300"/> Altın Üçgen Kariyer Pusulası</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-4">
                            <div className={`p-4 rounded-2xl border ${selectedStudent.holland ? 'bg-emerald-500/20 border-emerald-400/50' : 'bg-white/10 border-white/10'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm">Holland (İlgi)</span>
                                    {selectedStudent.holland ? <CheckCircle className="h-5 w-5 text-emerald-400"/> : <AlertCircle className="h-5 w-5 text-indigo-300 opacity-50"/>}
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl border ${selectedStudent.coklu_zeka ? 'bg-emerald-500/20 border-emerald-400/50' : 'bg-white/10 border-white/10'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm">Çoklu Zeka (Yetenek)</span>
                                    {selectedStudent.coklu_zeka ? <CheckCircle className="h-5 w-5 text-emerald-400"/> : <AlertCircle className="h-5 w-5 text-indigo-300 opacity-50"/>}
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl border ${selectedStudent.mesleki_degerler ? 'bg-emerald-500/20 border-emerald-400/50' : 'bg-white/10 border-white/10'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm">Değerler (Motivasyon)</span>
                                    {selectedStudent.mesleki_degerler ? <CheckCircle className="h-5 w-5 text-emerald-400"/> : <AlertCircle className="h-5 w-5 text-indigo-300 opacity-50"/>}
                                </div>
                            </div>
                        </div>

                        {isReady ? (
                            <button onClick={() => handleGenerateCompass(selectedStudent)} disabled={compassLoading} className="w-full py-4 bg-white hover:bg-indigo-50 text-indigo-700 rounded-xl font-black transition shadow-lg flex items-center justify-center">
                                {compassLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin"/> : <Compass className="h-5 w-5 mr-2"/>}
                                {compassLoading ? 'Yapay Zeka Pusulayı Çiziyor...' : 'Kariyer Pusulası Dergisi Üret'}
                            </button>
                        ) : (
                            <div className="w-full py-3 border border-dashed border-indigo-400/30 rounded-xl text-center text-indigo-300 font-bold text-sm">
                                Pusula Üretimi İçin Eksik Testlerin Çözülmesi Bekleniyor
                            </div>
                        )}
                    </div>
                </div>

                {compassResult && (
                    <div id="compass-print-area" className="bg-white p-8 rounded-3xl border border-indigo-100 shadow-sm print:border-none print:shadow-none print:p-0 mt-6">
                         <div className="border-b border-slate-100 pb-4 mb-6 flex justify-between items-center print:border-b-2 print:border-indigo-900">
                             <div>
                                 <h3 className="text-2xl font-black text-indigo-900 uppercase">Kariyer Pusulası</h3>
                                 <p className="text-slate-500 text-sm mt-1 font-bold print:text-indigo-700">{selectedStudent.class} - {selectedStudent.name}</p>
                             </div>
                             <div className="flex gap-2">
                                 <button onClick={() => handlePrint('compass-print-area', 'Kariyer Pusulası')} disabled={downloadingPdf} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs flex items-center transition shadow-md no-print">
                                     {downloadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Printer className="h-4 w-4 mr-2"/>}
                                     PDF / Yazdır
                                 </button>
                             </div>
                         </div>
                         <div className="prose prose-indigo max-w-none prose-h1:text-indigo-900 prose-h2:text-indigo-800 prose-p:text-slate-700">
                             <EditableAIBox text={compassResult} />
                         </div>
                    </div>
                )}

                {/* TEST ARŞİVİ */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden mt-6">
                    <div className="p-6 border-b border-slate-100">
                        <h3 className="font-bold text-slate-800 flex items-center"><Activity className="h-5 w-5 mr-2 text-slate-400"/> Öğrenci Test Arşivi ({selectedStudent.tests.length} Kayıt)</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-100">
                                <tr><th className="p-4">Test Adı</th><th className="p-4">Tarih</th><th className="p-4 text-right">İşlem</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {selectedStudent.tests.sort((a,b)=>new Date(b.timestamp) - new Date(a.timestamp)).map(t => (
                                    <tr key={t.id} className="hover:bg-slate-50 transition">
                                        <td className="p-4 font-bold text-slate-700">{t.testName}</td>
                                        <td className="p-4 text-slate-500">{new Date(t.timestamp).toLocaleDateString('tr-TR')}</td>
                                        <td className="p-4 text-right">
                                            <button onClick={() => onAnalyze(t)} className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg font-bold text-xs transition">Görüntüle</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // --- EKRAN 2: SINIF LİSTESİ ---
    if (selectedClassFolder) {
        const classStudents = studentsArray.filter(s => 
            s.class === selectedClassFolder && 
            (s.no.includes(searchTerm) || s.name.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        return (
            <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-4">
                    <div>
                        <button onClick={() => setSelectedClassFolder(null)} className="flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 transition mb-2">
                            <ChevronLeft className="h-4 w-4 mr-1"/> Klasörlere Dön
                        </button>
                        <h2 className="text-3xl font-black text-slate-800 flex items-center"><FolderOpen className="h-7 w-7 mr-3 text-blue-500"/> {selectedClassFolder} Sınıfı Dosyaları</h2>
                    </div>
                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="İsim veya No Ara..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                            <tr>
                                <th className="p-5">Öğrenci No</th>
                                <th className="p-5">Ad Soyad</th>
                                <th className="p-5 text-center">Durum</th>
                                <th className="p-5 text-right">Detay</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {classStudents.length === 0 ? (
                                <tr><td colSpan="4" className="p-10 text-center text-slate-400 font-medium">Bu sınıfta kayıtlı veya aramaya uygun öğrenci yok.</td></tr>
                            ) : (
                                classStudents.map(student => {
                                    const isReady = student.holland && student.coklu_zeka && student.mesleki_degerler;
                                    return (
                                        <tr key={student.id} onClick={() => setSelectedStudent(student)} className="hover:bg-blue-50 transition cursor-pointer group">
                                            <td className="p-5 font-black text-slate-700">{student.no}</td>
                                            <td className="p-5">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-900 flex items-center">
                                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold text-xs transition ${
                                                            student.masterProfile.isCritical ? 'bg-red-600 text-white animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.5)]' :
                                                            student.masterProfile.masterRisk === 'high' ? 'bg-rose-100 text-rose-600' :
                                                            student.masterProfile.masterRisk === 'medium' ? 'bg-amber-100 text-amber-600' :
                                                            'bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600'
                                                        }`}>
                                                            {student.masterProfile.isCritical ? <AlertTriangle className="h-4 w-4"/> : <User className="h-4 w-4"/>}
                                                        </div>
                                                        {student.name}
                                                        
                                                        {student.masterProfile.masterRisk !== 'unknown' && student.masterProfile.masterRisk !== 'low' && (
                                                            <span className={`ml-3 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${
                                                                student.masterProfile.isCritical ? 'bg-red-600 text-white border-red-700' :
                                                                student.masterProfile.masterRisk === 'high' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                                                'bg-amber-50 text-amber-700 border-amber-200'
                                                            }`}>
                                                                {student.masterProfile.isCritical ? '🚨 ÖNCELİKLİ VAKA' : `Risk Skoru: ${student.masterProfile.highestRiskScore}/100`}
                                                            </span>
                                                        )}
                                                    </span>
                                                    
                                                    {/* ALAN (DOMAIN) KIRILIMLARI VE PATTERNLER */}
                                                    <div className="flex flex-col gap-1 mt-2 ml-11">
                                                        {student.masterProfile.domainBreakdown && student.masterProfile.domainBreakdown.length > 0 && (
                                                            <div className="flex gap-2">
                                                                {student.masterProfile.domainBreakdown.map((dom, idx) => (
                                                                    <span key={idx} className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center ${
                                                                        dom.score >= 70 ? 'bg-rose-100 text-rose-700' :
                                                                        dom.score >= 40 ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-slate-100 text-slate-500'
                                                                    }`}>
                                                                        {dom.name}: {dom.score}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {student.masterProfile.patterns && student.masterProfile.patterns.length > 0 && (
                                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center bg-rose-100 text-rose-700 w-max mt-1">
                                                                {student.masterProfile.patterns[0]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-5 text-center">
                                                {isReady ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest"><CheckCircle className="h-3 w-3 mr-1"/> Pusula Hazır</span>
                                                ) : (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-widest"><AlertCircle className="h-3 w-3 mr-1"/> Veri Eksik</span>
                                                )}
                                            </td>
                                            <td className="p-5 text-right">
                                                <button className="text-blue-600 font-bold text-xs flex items-center justify-end w-full opacity-0 group-hover:opacity-100 transition-opacity">
                                                    Dosyayı Aç <ChevronRight className="h-4 w-4 ml-1"/>
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // --- EKRAN 1: SINIF KLASÖRLERİ (ANA EKRAN) ---
    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            <div className="mb-6">
                <h2 className="text-3xl font-black text-slate-800">Öğrenci Dosyaları (CRM)</h2>
                <p className="text-slate-500 font-medium mt-1">Sınıf bazlı öğrenci dijital dosyaları, Gözlem Notları ve Kariyer Pusulası.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {uniqueClasses.length === 0 ? (
                    <div className="col-span-full p-12 text-center text-slate-400 bg-white rounded-3xl border border-slate-200 border-dashed">
                        <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-20"/>
                        <p className="font-bold">Henüz incelenmiş öğrenci kaydı bulunmuyor.</p>
                    </div>
                ) : (
                    uniqueClasses.map(cls => (
                        <div key={cls} onClick={() => { setSelectedClassFolder(cls); setSearchTerm(''); }} className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-blue-500 hover:shadow-lg transition cursor-pointer group flex flex-col items-center text-center">
                            <div className="h-16 w-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-sm">
                                <FolderOpen className="h-8 w-8"/>
                            </div>
                            <h3 className="font-black text-xl text-slate-800 mb-1">{cls}</h3>
                            <p className="text-xs font-bold text-slate-400 group-hover:text-blue-500 transition">Klasörü Aç</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default StudentFolders;